import { useCallback } from "react";
import { useSession } from "@hooks/useSession";
import { useLocalStorage } from "../../../shared/hooks/useLocalStorage";

/**
 * Which payroll-settings steps the payer has saved.
 *
 * v1 recorded each one on the user's `activity.flows`
 * (`onboardingPayrollSettings`, `onboardingPayableSettings`), and the module
 * reported itself complete once both were set. That resource is retired and v3
 * records no onboarding progress: `PayerSettings.payrollSettings` carries the
 * payer's answer but not the fact that they answered (`runsPayroll` is served
 * for every payer), and `InvoicingConfig` projects empty `events` and `actors`
 * even for a payer whose config has been written — so neither step's completion
 * is readable from the API. The flags therefore live in localStorage, keyed by
 * account, the same treatment retired activity flags got in
 * {@link useNudgeDismissal}.
 *
 * Trade-off vs. v1: progress no longer follows the payer across devices or
 * browsers, so a payer resuming onboarding elsewhere walks both steps again with
 * their saved settings pre-filled. Erring that way is deliberate — the opposite
 * error would skip a step the payer never completed.
 */
export const usePayrollSettingsProgress = () => {
  const { accountId } = useSession();

  const [payrollScheduleSaved, setPayrollScheduleSaved] = useLocalStorage(
    `onboarding-saved:payrollSchedule:${accountId}`,
    false
  );
  const [payablesSettingsSaved, setPayablesSettingsSaved] = useLocalStorage(
    `onboarding-saved:payablesSettings:${accountId}`,
    false
  );

  const markPayrollScheduleSaved = useCallback(() => {
    setPayrollScheduleSaved(true);
  }, [setPayrollScheduleSaved]);

  const markPayablesSettingsSaved = useCallback(() => {
    setPayablesSettingsSaved(true);
  }, [setPayablesSettingsSaved]);

  return {
    // Before the session resolves there is no account to scope the flags to, so
    // treat the module as unstarted rather than reading a shared key.
    isComplete: !!accountId && payrollScheduleSaved && payablesSettingsSaved,
    markPayrollScheduleSaved,
    markPayablesSettingsSaved
  };
};
